import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom/client'
import { useReactToPrint } from 'react-to-print'
import Barcode from 'react-barcode'
import { RLabel } from '../../models'
import { readableDateFormat } from '../../../../utils/dateUtils'
import './PrintLabelPDF.css'
import { toast } from 'react-toastify'

type Props = {
  data: RLabel
  onDone: () => void
}

const LabelBlock = ({ data, date }: { data: RLabel; date: string }) => {
  return (
    <div className='print-label-block'>
      <div className='print-label-header'>
        <span className='print-label-name'>{data?.name}</span>
        <span className='print-label-meta'>{data?.gender}</span>
        <span className='print-label-meta'>{data?.age}</span>
      </div>
      <div className='print-label-subheader'>
        <span className='print-label-date'>{data?.patientId}</span>
        <span className='print-label-date'>{date}</span>
      </div>
      <div className='print-label-barcode-wrapper'>
        <Barcode
          value={'' + data?.code}
          height={22}
          width={1.2}
          margin={2}
          fontSize={6}
          displayValue={true}
          background='#ffffff'
          lineColor='#000000'
        />
      </div>
    </div>
  )
}

const PrintLabelTrigger = ({ data, onDone }: Props) => {
  const printRef = useRef<HTMLDivElement>(null)
  const date = readableDateFormat(new Date())

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
      @page { size: 50mm 25mm; margin: 0; }
      @media print {
        html, body { margin: 0; padding: 0; width: 50mm; height: 25mm; }
        * { box-sizing: border-box; }
      }
    `,
    onAfterPrint: onDone,
  })

  const handlePrintRef = useRef(handlePrint)

  useEffect(() => {
    handlePrintRef.current()
  }, [])

  return (
    <div className='print-label-hidden'>
      <div ref={printRef} className='print-label-content'>
        <div className='print-label-page-break'>
          <LabelBlock data={data} date={date} />
        </div>
        <LabelBlock data={data} date={date} />
      </div>
    </div>
  )
}

/**
 * Renders a hidden printable label template and opens the browser print flow.
 * Cleans up the temporary DOM container after printing finishes.
 */
export const printLabelPDF = (data: RLabel): void => {
  if (!data) {
    console.error('Print data is required')
    return
  }

  try {
    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.top = '-9999px'
    container.style.left = '-9999px'
    container.style.pointerEvents = 'none'
    document.body?.appendChild(container)
    const root = ReactDOM.createRoot(container)

    const cleanup = () => {
      try {
        root.unmount()
      } catch (error) {
        console.warn('Error unmounting print component:', error)
      }
      if (document.body.contains(container)) {
        document.body.removeChild(container)
      }
    }
    root.render(<PrintLabelTrigger data={data} onDone={cleanup} />)
  } catch (error) {
    console.error('Error during print process:', error)
    toast.error(
      'An error occurred while trying to print the label. Please try again.'
    )
  }
}
